import { apiFetch } from './client.ts';
import type { Unlock } from '@ricochet/achievements';

export interface ApiPuzzle {
  id: string;
  name: string;
  encoded: string;
  gridSize: number;
  par: number;
  createdAt: string;
  author: { id: string | null; username: string; country: string; avatarFilename: string | null } | null;
  likeCount: number;
  solveCount: number;
  liked?: boolean;
  userBest?: number | null;
  overallBest?: number | null;
}

interface PuzzleListResponse {
  puzzles: ApiPuzzle[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ToggleLikeResponse {
  liked: boolean;
  likeCount: number;
  unlockedAchievements?: Unlock[];
}

interface UploadPuzzleResponse {
  id: string;
  name: string;
  encoded: string;
  gridSize: number;
  par: number;
  authorId: string;
  authorName: string;
  createdAt: string;
  unlockedAchievements?: Unlock[];
}

export function listPuzzles(
  params?: { page?: number; limit?: number; sort?: 'newest' | 'popular' | 'most_played'; search?: string; filter?: 'all' | 'not_solved' | 'not_bested' },
  token?: string,
): Promise<PuzzleListResponse> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.sort) query.set('sort', params.sort);
  if (params?.search) query.set('search', params.search);
  if (params?.filter && params.filter !== 'all') query.set('filter', params.filter);
  const qs = query.toString();
  return apiFetch(`/api/puzzles${qs ? `?${qs}` : ''}`, { token });
}

export function getMyLikes(
  params?: { page?: number; limit?: number },
  token?: string,
): Promise<PuzzleListResponse> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return apiFetch(`/api/users/me/likes${qs ? `?${qs}` : ''}`, { token });
}

export function uploadPuzzle(
  data: { puzzle: { name: string; encoded: string }; solution: string },
  token: string,
): Promise<UploadPuzzleResponse> {
  return apiFetch('/api/puzzles', { method: 'POST', body: data, token, timeoutMs: 12000 });
}

export interface SolveResponse {
  id: string;
  puzzleId: string;
  numberOfMoves: number;
  createdAt: string;
  isNewBest: boolean;
  unlockedAchievements?: Unlock[];
}

export interface PuzzleSolve {
  id: string;
  numberOfMoves: number;
  createdAt: string;
  user: { id: string; username: string; avatarFilename: string | null };
}

interface PuzzleSolvesResponse {
  solves: PuzzleSolve[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export function getPuzzleSolves(
  puzzleId: string,
  params?: { page?: number; limit?: number },
  token?: string,
): Promise<PuzzleSolvesResponse> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return apiFetch(`/api/puzzles/${puzzleId}/solves${qs ? `?${qs}` : ''}`, { token });
}

export function submitSolve(
  puzzleId: string,
  moveSequence: string,
  token?: string,
): Promise<SolveResponse> {
  return apiFetch(`/api/puzzles/${puzzleId}/solve`, { method: 'POST', body: { moveSequence }, token });
}

export function toggleLike(
  puzzleId: string,
  token: string,
): Promise<ToggleLikeResponse> {
  return apiFetch(`/api/puzzles/${puzzleId}/like`, { method: 'POST', token });
}

export interface DailyPuzzleResponse {
  id: string;
  name: string;
  encoded: string;
  gridSize: number;
  numTargets: number;
  difficulty: number;
  par: number;
  createdAt: string;
  author: { id: string | null; username: string; country: string };
  likeCount: number;
  solveCount: number;
  overallBest: number | null;
  userBest?: number | null;
  solved?: boolean;
  liked?: boolean;
}

export function getPuzzle(id: string, token?: string): Promise<ApiPuzzle> {
  return apiFetch(`/api/puzzles/${id}`, { token });
}

export function fetchDailyPuzzle(token?: string): Promise<DailyPuzzleResponse> {
  return apiFetch('/api/puzzles/today', { token });
}

export interface DailyStreakEntry {
  puzzleId: string;
  name: string;
  encoded: string;
  gridSize: number;
  numTargets: number | null;
  difficulty: number | null;
  par: number;
  date: string;
  userMoves: number | null;
  overallBest: number | null;
}

export function getDailyStreak(token: string): Promise<{ dailySolves: DailyStreakEntry[] }> {
  return apiFetch('/api/users/me/daily-solves?limit=4', { token });
}
